'use client'

import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { getContainerLogs } from "@/app/actions/docker"
import { FileText, RefreshCw } from "lucide-react"

interface ContainerLogsProps {
    containerId: string
    containerName: string
}

export function ContainerLogs({ containerId, containerName }: ContainerLogsProps) {
    const [logs, setLogs] = useState<string>('')
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)

    const fetchLogs = async () => {
        setLoading(true)
        try {
            const data = await getContainerLogs(containerId)
            setLogs(data)
        } catch (error) {
            console.error("Failed to fetch logs", error)
            setLogs("Failed to fetch logs.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (open) {
            fetchLogs()
        }
    }, [open])

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                    <FileText className="mr-2 h-4 w-4" /> Logs
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="pr-8">
                        Logs: {containerName}
                    </DialogTitle>
                    <DialogDescription>
                        Viewing last 100 lines of logs.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end mb-2">
                    <Button variant="ghost" size="sm" onClick={fetchLogs} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
                <ScrollArea className="flex-1 w-full rounded-md border bg-black text-white font-mono text-xs overflow-hidden">
                    <div className="p-4 min-h-0">
                        <pre className="whitespace-pre-wrap break-all">
                            {logs || "No logs available."}
                        </pre>
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    )
}
